import { PlusOutlined } from '@ant-design/icons'

import {
  Button,
  EmptyState,
  NoDataIcon,
  PageContentWrapper,
  PageHeader,
  Search
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useRequestedHomeSwitch } from '@shared/hooks/useRequestedHomeSwitch'
import actions from 'actions'
import { Alert } from 'antd'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import dayjs, { type Dayjs } from 'dayjs'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { debounce } from 'lodash'
import { type ChangeEventHandler, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import type { RangeValue } from 'types/shared'
import DayCard from '../components/DayCard'
import DayNavigation from '../components/DayNavigation'
import SortDailyTasks from '../components/SortDailyTasks'
import TaskFilters, { type TaskFiltersValue } from '../components/TaskFilters'
import useDailyTasks from '../hooks/useDailyTasks'
import useToDosFilterParams from '../hooks/useFilterToDosParams'
import { ADD_TODO_HASH_ROUTE, initialFiltersValue } from '../utils/managerUtils'

const HELP_ARTICLE_URL =
  'https://support.logmycare.co.uk/en/articles/1571162-how-can-i-see-and-edit-the-status-of-today-s-to-do-s-in-one-place'

const SHOWN_FUTURE_TASKS_HELP_URL =
  'https://support.logmycare.co.uk/en/articles/8348676-re-generating-to-dos'

interface NewDashboardProps {
  testId?: string
}

const NewDashboard = ({ testId }: NewDashboardProps): JSX.Element => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [showDayNavigation, setShowDayNavigation] = useState(true)
  const [sortOrder, setSortOrder] = useState<'-1' | '1'>('-1')

  const location = useLocation()

  const [filterParams, setFilterParams] =
    useToDosFilterParams(initialFiltersValue)

  useRequestedHomeSwitch()

  const {
    isLoading: isQueryLoading,
    days,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchedAfterMount
  } = useDailyTasks({
    ...filterParams,
    sortOrder
  })

  const isLoading = isQueryLoading || !isFetchedAfterMount

  const { t } = useTranslation(NAMESPACE.TODOS)

  const handleNextDay = () => {
    setFilterParams((prevFilterParams) => ({
      ...prevFilterParams,
      rangePicker: prevFilterParams.rangePicker?.map((dateValue) =>
        dateValue?.add(1, 'day')
      ) as RangeValue<Dayjs>
    }))
    window.scrollTo(0, 0)
  }

  const onFilterChange = (value: TaskFiltersValue) => {
    const newValue = { ...value }

    if (newValue.rangePicker) {
      if (
        dayjs(newValue.rangePicker[0])?.toString() !==
        dayjs(newValue.rangePicker[1])?.toString()
      ) {
        setShowDayNavigation(false)
      } else {
        setShowDayNavigation(true)
      }
    } else {
      newValue.rangePicker = initialFiltersValue.rangePicker

      setShowDayNavigation(true)
    }

    setFilterParams((prevFilters) => ({
      ...prevFilters,
      ...newValue
    }))
  }

  const handlePreviousDay = () => {
    setFilterParams((prevFilterParams) => ({
      ...prevFilterParams,
      rangePicker: prevFilterParams.rangePicker?.map((dateValue) =>
        dateValue?.subtract(1, 'day')
      ) as RangeValue<Dayjs>
    }))
    window.scrollTo(0, 0)
  }

  const handleOpenModal = () => {
    window.scrollTo(0, 0)
    dispatch(
      actions.openModal({
        modalId: 'createTask',
        data: {
          onComplete: () => {
            refetch()
            navigate(location.pathname)
          }
        }
      })
    )
    dispatch(actions.addForm({ formId: 'createTask' }))
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  useEffect(() => {
    if (location.hash.includes(ADD_TODO_HASH_ROUTE)) {
      handleOpenModal()
    }
  }, [location.hash])

  const debouncedSaved = debounce(() => {
    fetchNextPage()
  }, 512)
  const debouncedSearch = debounce((searchValue: string) => {
    setFilterParams((prev) => ({
      ...prev,
      search: searchValue
    }))
  }, 1024)
  const onSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    debouncedSearch(event.target.value.trim())
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = document.documentElement
      const threshold = 50

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        debouncedSaved()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [days])

  const maxGeneratedTasksDate = dayjs().add(1, 'month')
  const showUngeneratedTasksMessage = filterParams.rangePicker?.[1]?.isAfter(
    maxGeneratedTasksDate
  )
  const hasRolePermission = useAbilityAccess([
    {
      i: 'create',
      this: 'TaskManagement'
    }
  ])

  const hasResults = !!days.length

  return (
    <Wrapper data-testid={testId || 'todos-dashboard'}>
      <PageHeader
        title={
          <TitleContainer
            data-testid={testId ? `${testId}-title` : 'todos-dashboard-title'}
          >
            {t(toDosConstants.DASHBOARD_TITLE)}
          </TitleContainer>
        }
        moreInfoLink={HELP_ARTICLE_URL}
        data-testid={
          testId ? `${testId}-page-header` : 'todos-dashboard-page-header'
        }
      >
        <RightHeaderWrapper
          data-testid={
            testId
              ? `${testId}-header-actions`
              : 'todos-dashboard-header-actions'
          }
        >
          <Search
            placeholder={t(toDosConstants.SEARCH_BY_TITLE)}
            onChange={onSearch}
            data-testid={testId ? `${testId}-search` : 'todos-dashboard-search'}
          />
          <ButtonHeaderStyled
            onClick={handleOpenModal}
            variant="primary"
            icon={<PlusOutlined />}
            data-cy="btn-add-todo"
            data-testid={
              testId
                ? `${testId}-add-todo-button`
                : 'todos-dashboard-add-todo-button'
            }
            disabledByPermission={!hasRolePermission}
          >
            {t(toDosConstants.ADD_A_TODO)}
          </ButtonHeaderStyled>
        </RightHeaderWrapper>
      </PageHeader>
      <div
        data-testid={
          testId
            ? `${testId}-filters-section`
            : 'todos-dashboard-filters-section'
        }
      >
        <TaskFilters
          onChange={onFilterChange}
          value={filterParams}
          testId={
            testId ? `${testId}-task-filters` : 'todos-dashboard-task-filters'
          }
        />
        {showUngeneratedTasksMessage && (
          <StyledAlert
            showIcon
            message={
              <Text level={2} weight="medium">
                {t(toDosConstants.RECURRING_DISCLAIMER)}
                <Link to={SHOWN_FUTURE_TASKS_HELP_URL}>
                  {t(toDosConstants.HERE).concat('.')}
                </Link>
              </Text>
            }
            closable
            data-testid={
              testId
                ? `${testId}-ungenerated-tasks-alert`
                : 'todos-dashboard-ungenerated-tasks-alert'
            }
          />
        )}
      </div>
      {!showDayNavigation && (
        <SortDailyTasks
          setSortOrder={setSortOrder}
          data-testid={
            testId
              ? `${testId}-sort-daily-tasks`
              : 'todos-dashboard-sort-daily-tasks'
          }
        />
      )}
      {isLoading ? (
        <Spinner />
      ) : (
        <DashboardContent
          data-testid={testId ? `${testId}-content` : 'todos-dashboard-content'}
        >
          {!isLoading && hasResults && (
            <DaysWrapper
              data-testid={
                testId
                  ? `${testId}-days-wrapper`
                  : 'todos-dashboard-days-wrapper'
              }
            >
              {days?.map((day, index) => (
                <DayCard
                  day={day}
                  key={day.date.toString()}
                  filterParams={filterParams}
                  refetchDailyTasks={refetch}
                  data-testid={
                    testId
                      ? `${testId}-day-card-${index}`
                      : `todos-dashboard-day-card-${index}`
                  }
                />
              ))}
            </DaysWrapper>
          )}
          {!isLoading && !hasResults && (
            <EmptyState
              icon={<NoDataIcon />}
              title={t(toDosConstants.NO_TASKS)}
              description={t(toDosConstants.NO_TASKS_DESCRIPTION)}
              data-testid={
                testId ? `${testId}-empty-state` : 'todos-dashboard-empty-state'
              }
            />
          )}

          {showDayNavigation && (
            <DayNavigation
              handlePreviousDay={handlePreviousDay}
              handleNextDay={handleNextDay}
              testId={
                testId
                  ? `${testId}-day-navigation`
                  : 'todos-dashboard-day-navigation'
              }
            />
          )}
        </DashboardContent>
      )}
      {hasNextPage && <Spinner />}
    </Wrapper>
  )
}

const GAP_BETWEEN_ELEMENTS = 24

const StyledAlert = styled(Alert)`
  margin-top: 12px;
  color: ${(props) => props.theme.color.neutral.color4};
`

const Wrapper = styled(PageContentWrapper).attrs({
  $gap: GAP_BETWEEN_ELEMENTS,
  $paddingTop: 24,
  $paddingX: 24,
  $paddingBottom: 50
})`
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    padding: 24px;
  }
`

const DaysWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const RightHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ButtonHeaderStyled = styled(Button)`
  white-space: nowrap;
`

const DashboardContent = styled.div``

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

export default NewDashboard
